<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isPostPinned } from '@@/bits/post_state'
import { defineAsyncComponent } from '@@/bits/vue'
import { isSectionGroup } from '@@/bits/wish_arrangement'
import { WallWishGroupByValue, WallWishSortByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge } from '@@/pragmatic-drag-and-drop/helpers'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, Post, WallGroupPost } from '@@/types'
import SurfacePostsTimelineSectionHeader from '@@/vuecomponents/SurfacePostsTimelineSectionHeader.vue'
import SurfacePostTimeline from '@@/vuecomponents/SurfacePostTimeline.vue'
import SurfaceTimelineAddPostButton from '@@/vuecomponents/SurfaceTimelineAddPostButton.vue'
import SurfaceTimelineDashLine from '@@/vuecomponents/SurfaceTimelineDashLine.vue'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    postBeingEditedCid: Cid | null
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
    canShowAddPostButton: boolean
    isMobileLandscapeScreen: boolean
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const { isSectioned, backgroundLuminance, colorScheme, isReadOnly, isFrozen } = storeToRefs(useSurfaceStore())
const surfacePostsStore = useSurfacePostsStore()
const { allSortedPostsByGroup, allSortedPosts } = storeToRefs(surfacePostsStore)
const { startNewPost, shouldDisplayPost, shouldDisplayGroup } = surfacePostsStore
const {
  sortedGroups,
  originalWishSortBy,
  isGroupByNone,
  isGroupBySection,
  isGroupByColor,
  isGroupBySingleSelect,
  customPropIdFromWishGroupBy,
} = storeToRefs(useSurfaceWishArrangementStore())
const { isAGroupDragging, addPostButtonDisabledTooltipText } = storeToRefs(useSurfacePostGroupByStore())
const { dashedLineMarginPercentFromViewportBottom, dashedLineMarginBottomClasses } = storeToRefs(
  useSurfaceTimelineFormatStore(),
)
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())

// State to hide 2 dashed lines if the drop indicator is on top of the dashed line
const dashedLineIdToHide = ref<Cid | Id | null>(null)
const closestEdgeToPointer = ref<LogicalEdge | null>(null)

const isPostDroppable = computed(() => {
  return (
    originalWishSortBy.value === WallWishSortByTypes.ManualNewPostsFirst ||
    originalWishSortBy.value === WallWishSortByTypes.ManualNewPostsLast
  )
})

const lastPinnedPostIdByGroup = computed(() => {
  return Object.fromEntries(
    Object.entries(allSortedPostsByGroup.value).map(([group, posts]) => {
      if (posts == null) {
        return [group, undefined]
      }
      const pinnedPosts = posts.filter((post) => isPostPinned(post))
      if (pinnedPosts.length === 0) {
        return [group, undefined]
      }
      const lastPinnedPost = pinnedPosts[pinnedPosts.length - 1]
      return [group, lastPinnedPost.id]
    }),
  )
})

// #region - Add post
const addPostAfter = (post: Post | null, groupId?: WallGroupPost['id']): void => {
  // Timeline sort index is from smallest on the left to largest to the right
  let nextId: Id | null | undefined
  let previousId: Id | null | undefined
  let wallSectionId: number | null | undefined
  if (post === null) {
    wallSectionId = null
    nextId = null
    previousId = null
  } else {
    // Determine index of post
    wallSectionId = post.wall_section_id
    const postsInGroup = isSectioned.value
      ? (allSortedPostsByGroup.value[wallSectionId as number] ?? [])
      : allSortedPosts.value
    const postIndex = postsInGroup.findIndex((p) => p.id === post.id)
    if (postIndex === postsInGroup.length - 1) {
      // Post is added to the end of the timeline
      nextId = null
      previousId = postsInGroup[postIndex].id
    } else {
      // Sandwiched in between
      nextId = postsInGroup[postIndex + 1].id
      previousId = postsInGroup[postIndex].id
    }
  }
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: previousId === null ? null : `wish-${previousId}`,
    nextId: nextId === null ? null : `wish-${nextId}`,
  })
  startNewPost({
    attributes: {
      wall_section_id: isGroupBySection.value && wallSectionId != null ? wallSectionId : undefined,
      sortIndex: sortIndex ?? undefined,
      color: isGroupByColor.value && post?.color != null ? post.color : undefined,
      custom_properties:
        isGroupBySingleSelect.value &&
        customPropIdFromWishGroupBy.value != null &&
        groupId != null &&
        groupId !== WallWishGroupByValue.NoValue
          ? { [customPropIdFromWishGroupBy.value]: groupId }
          : undefined,
    },
  })
}

const getAddPostButtonDisabledTooltipText = (groupId: WallGroupPost['id']): string | undefined => {
  if (groupId === WallWishGroupByValue.NoValue) {
    return addPostButtonDisabledTooltipText.value
  }
  return undefined
}
// #endregion

// #region Scroll to new post
const containerTimelinePostsRef = ref<HTMLElement | null>(null)

useScrollToNewestPost(containerTimelinePostsRef)
// #endregion
</script>

<template>
  <div ref="containerTimelinePostsRef" class="flex h-full">
    <template v-if="isAppUsing('groupPostsV2') ? !!isGroupByNone : !isSectioned">
      <div
        key="no-section-start-dashed-line"
        :class="[
          'self-end flex items-center gap-0.5',
          isMobileLandscapeScreen && ['mb-6.5'],
          !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in 3"
          :key="`no-section-start-dashed-line-${n}`"
          class="mb-[9.5px]"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
      <SurfaceTimelineAddPostButton
        v-if="canShowAddPostButton"
        class="self-end"
        :label="__('Add post to padlet')"
        :is-mobile-landscape-screen="isMobileLandscapeScreen"
        :background-luminance="backgroundLuminance"
        :color-scheme="colorScheme"
        @click="addPostAfter(null)"
      />
      <div
        v-else
        :class="[
          'self-end flex items-center gap-0.5 mx-0.5',
          isMobileLandscapeScreen && ['mb-[35.5px]'],
          !isMobileLandscapeScreen && [
            'mb-[153.5px]',
            device.app && '@tablet-portrait/surface-container:mb-[225.5px]',
            !device.app && [
              dashedLineMarginPercentFromViewportBottom === 0.25 &&
                '@tablet-portrait/surface-container:mb-[calc(100vh*0.25+9.5px)]',
              dashedLineMarginPercentFromViewportBottom === 0.36 &&
                '@tablet-portrait/surface-container:mb-[calc(100vh*0.36+9.5px)]',
            ],
          ],
          isPostDroppable && closestEdgeToPointer != null && dashedLineIdToHide === null && 'opacity-0',
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in 2"
          :key="`no-section-add-post-dashed-line-${n}`"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
    </template>
    <section
      v-for="(group, index) in sortedGroups"
      v-show="shouldDisplayGroup(group)"
      :id="`section-${group.id}`"
      :key="group.id"
      :data-id="group.id"
      :data-rank="isSectionGroup(group) ? group.sort_index : undefined"
      :aria-labelledby="isSectionGroup(group) ? group.ariaLabelledby : undefined"
      class="flex items-end"
    >
      <!-- Need to check if `group.id !== 'none'` because the `isSectioned` remains `true` for a very short time when the section is toggled to off. -->
      <!-- If we don't add this check, the section header will be rendered with the wrong section prop and it will throw an console error since the section header is expecting a `LabelledSection` prop instead of `{ id: 'none' }`. -->
      <SurfacePostsTimelineSectionHeader
        v-if="(isAppUsing('groupPostsV2') ? !isGroupByNone : isSectioned) && group.id !== 'none'"
        :key="group.id"
        :section="group"
        :all-sorted-posts-by-group="allSortedPostsByGroup"
        :hide-buttons="true"
        :x-start-skewed-edge="index !== 0"
        :color-scheme="colorScheme"
        :background-luminance="backgroundLuminance"
        :can-show-add-post-button="canShowAddPostButton"
        :dashed-line-id-to-hide="dashedLineIdToHide"
        :is-mobile-landscape-screen="isMobileLandscapeScreen"
        @dashed-line-id-to-hide="dashedLineIdToHide = $event"
        @closest-edge-to-pointer="closestEdgeToPointer = $event"
      />
      <div :id="`group-posts-${group.id}`" class="flex h-full">
        <div
          v-for="(post, postIndex) in allSortedPostsByGroup[group.id]"
          v-show="!isAGroupDragging && shouldDisplayPost(post.cid)"
          :key="post.cid"
          :data-post-cid="post.cid"
          class="flex items-end"
        >
          <SurfacePostTimeline
            class="grow"
            :post="post"
            :index="postIndex"
            :is-last-pinned-post="post.id === lastPinnedPostIdByGroup[group.id]"
            :reaction-ids-by-post-id="reactionIdsByPostId"
            :comment-ids-by-post-id="commentIdsByPostId"
            :posts-in-group="allSortedPostsByGroup[group.id] || []"
            :is-mobile-landscape-screen="isMobileLandscapeScreen"
            @dashed-line-id-to-hide="dashedLineIdToHide = $event"
            @closest-edge-to-pointer="closestEdgeToPointer = $event"
          />
          <SurfaceTimelineAddPostButton
            v-if="canShowAddPostButton"
            :label="
              isSectionGroup(group)
                ? __('Add post to the section %{sectionTitle}', { sectionTitle: group.title })
                : __('Add post to padlet')
            "
            :disabled-tooltip-text="getAddPostButtonDisabledTooltipText(group.id)"
            :is-mobile-landscape-screen="isMobileLandscapeScreen"
            :background-luminance="backgroundLuminance"
            :color-scheme="colorScheme"
            @click="addPostAfter(post, group.id)"
          />
          <div
            v-else
            :class="[
              'flex items-center gap-0.5 mx-0.5',
              isMobileLandscapeScreen && ['mb-6.5'],
              !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
              isPostDroppable && dashedLineIdToHide === post.cid && 'opacity-0',
            ]"
          >
            <SurfaceTimelineDashLine
              v-for="n in 2"
              :key="`${post.cid}-add-post-dashed-line-${n}`"
              class="mb-[9.5px]"
              :dark-mode="backgroundLuminance === 'dark'"
            />
          </div>
        </div>
      </div>

      <div
        :class="[
          'flex items-end gap-0.5',
          isMobileLandscapeScreen && ['mb-6.5'],
          !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in 4"
          :key="`${group.id}-dashed-line-end-${n}`"
          class="mb-[9.5px]"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
    </section>
    <div
      v-show="!isAGroupDragging"
      v-if="
        (isGroupBySection || isGroupBySingleSelect) &&
        canIModerate &&
        !isReadOnly &&
        !isFrozen &&
        !isDemoPadletPanelDesktop
      "
      :class="[
        'flex items-end gap-0.5',
        isMobileLandscapeScreen && ['mb-6.5'],
        !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
      ]"
    >
      <SurfaceAddSectionButton class="self-end -mb-1" />
      <SurfaceTimelineDashLine
        v-for="n in 2"
        :key="`add-section-button-dashed-line-${n}`"
        class="mb-[9.5px]"
        :dark-mode="backgroundLuminance === 'dark'"
      />
    </div>
  </div>
</template>
